import * as React from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { z } from 'zod';
import { notifyAPIClient, notifyFailure } from '../../utils/toast';
import { apiPost, apiPut } from '../../utils/apiClient';
import {
  CourseLevel,
  CourseStage,
  SuccessResponse,
} from '../../types/CourseStageAccordion.type';
import { Subtype } from '../../types/ServiceAccordion.type';

type CourseStageDialogProps = {
  open: boolean;
  handleClose: () => void;
  courseStage: CourseStage | null;
  courseLevel: CourseLevel;
  courseType: Subtype;
  fetchCourses: () => void;
};

const CourseStageInput = z.object({
  id: z.number().int().optional(),
  subtypeId: z.number().int().optional(),
  level: z.number().int(),
  levelName: z.string(),
  stageName: z
    .string()
    .min(1, 'Level stage name is required')
    .refine((val) => val.length === 0 || val.trim().length > 0, {
      message: 'Level stage name cannot be just whitespace',
    }),
  videoUrl: z.string().nullable().optional(),
});
type CourseStageInputType = z.infer<typeof CourseStageInput>;

const CourseStageDialog = ({
  open,
  handleClose,
  courseStage,
  courseLevel,
  courseType,
  fetchCourses,
}: CourseStageDialogProps) => {
  const [formError, setFormError] = React.useState<string | null>(null);

  const handleConfirm = async (data: CourseStageInputType) => {
    try {
      if (courseStage) {
        // Update existing stage
        const updateRes = await apiPut<SuccessResponse, CourseStageInputType>(
          '/course/stage/update',
          data,
        );
        notifyAPIClient(updateRes.message);
      } else {
        // Create new stage
        const createRes = await apiPost<SuccessResponse, CourseStageInputType>(
          '/course/stage/create',
          data,
        );
        notifyAPIClient(createRes.message);
      }
      fetchCourses();
      handleClose();
    } catch (error) {
      notifyFailure();
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    try {
      const data = CourseStageInput.parse({
        id: courseStage?.id || 0,
        subtypeId: Number(courseType.id),
        level: courseLevel.level,
        levelName: courseLevel.levelName,
        stageName: formData.get('stageName') as string,
        videoUrl: formData.get('videoUrl') || null,
      });
      setFormError(null);
      handleConfirm(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        setFormError(error.errors[0].message);
      } else {
        console.error(error);
      }
    }
  };

  return (
    <Dialog fullWidth open={open} onClose={handleClose}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Add new stage for this level</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="level"
            name="level"
            className="required-field"
            label="Level"
            type="number"
            fullWidth
            variant="standard"
            defaultValue={courseLevel.level}
            disabled
          />

          <TextField
            autoFocus
            margin="dense"
            id="levelName"
            name="levelName"
            className="required-field"
            label="Level Name"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={courseLevel.levelName}
            disabled
          />

          <TextField
            autoFocus
            margin="dense"
            id="stageName"
            name="stageName"
            className="required-field"
            label="Stage Name"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={courseStage?.stageName || ''}
            error={Boolean(formError)}
            helperText={formError}
          />

          <TextField
            autoFocus
            margin="dense"
            id="videoUrl"
            name="videoUrl"
            label="Video URL"
            type="text"
            fullWidth
            variant="standard"
            defaultValue={courseStage?.videoUrl || ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit">Confirm</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CourseStageDialog;
